import { Box, Tab, Tabs } from "@mui/material";
import type { JSX } from "react";
import { useSearchParams } from "react-router-dom";

import { CreatePage } from "../agent/CreatePage.js";
import { IdeasPage } from "../agent/IdeasPage.js";
import { AutonomousDraftingPanel } from "../settings/AutonomousDraftingPanel.js";

/**
 * Content studio — the left half of the Workbench. One segmented control over
 * the operator's THREE ways to create posts, all of which land in the approve
 * queue on the right:
 *
 *   1. Autonomous drafting — let the agent draft from trends on its own.
 *   2. Idea generator      — the agent suggests topics; you pick / brainstorm.
 *   3. On-demand drafting   — write a brief + pick the type; generate now.
 *
 * Each tab renders the EXISTING, unchanged surface (the Settings autonomous
 * switch, the Ideas feed, the Create-New form) so there is zero behaviour
 * drift — this is purely a relocation into one cockpit. The active tab is
 * mirrored to `?studio=` so it deep-links and the legacy `/marketing/agent/
 * create` + `/ideas` routes redirect straight into the right tab.
 */
export type StudioMode = "autonomous" | "ideas" | "ondemand";

const MODES: ReadonlyArray<{ key: StudioMode; label: string }> = [
  { key: "autonomous", label: "Autonomous drafting" },
  { key: "ideas", label: "Idea generator" },
  { key: "ondemand", label: "On-demand drafting" },
];

const MODE_KEYS = MODES.map((m) => m.key);

function paramToMode(v: string | null): StudioMode {
  return (MODE_KEYS as string[]).includes(v ?? "") ? (v as StudioMode) : "autonomous";
}

export function ContentStudio(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const mode = paramToMode(params.get("studio"));

  const setMode = (next: StudioMode): void => {
    const p = new URLSearchParams(params);
    p.set("studio", next);
    // replace: switching modes shouldn't pile up browser history entries.
    setParams(p, { replace: true });
  };

  return (
    <Box
      data-testid="content-studio"
      sx={{ display: "grid", gap: 1.5, alignContent: "start" }}
    >
      <Box>
        {/* The app-wide segmented tab control (theme `MuiTabs` override):
            a soft track with the active mode lifted out as a sliding paper
            pill, so it reads unmistakably as TABS (not headings). */}
        <Tabs
          value={mode}
          onChange={(_, v) => setMode(v as StudioMode)}
          variant="fullWidth"
          aria-label="Ways to create content"
        >
          {MODES.map((m) => (
            <Tab
              key={m.key}
              value={m.key}
              label={m.label}
              data-testid={`studio-tab-${m.key}`}
              id={`studio-tab-${m.key}`}
              aria-controls={`studio-panel-${m.key}`}
            />
          ))}
        </Tabs>
      </Box>

      <Box
        role="tabpanel"
        id={`studio-panel-${mode}`}
        aria-labelledby={`studio-tab-${mode}`}
        data-testid={`studio-panel-${mode}`}
      >
        {mode === "autonomous" && <AutonomousDraftingPanel />}
        {mode === "ideas" && <IdeasPage />}
        {mode === "ondemand" && <CreatePage />}
      </Box>
    </Box>
  );
}
